import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KeyRound, ArrowLeft, Loader2, Check, LogOut } from "lucide-react";
import { toast } from "sonner";
import { PasswordInput } from "@/components/PasswordInput";

export default function TrocarSenha() {
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [novo, setNovo] = useState("");
  const [confirmar, setConfirmar] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (novo.length < 6) return toast.error("Nova senha precisa ter pelo menos 6 caracteres");
    if (novo !== confirmar) return toast.error("As senhas não coincidem");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: novo,
      });
      toast.success("Senha alterada com sucesso");
      const u = await refresh();
      // Se estava com senha provisória (1º login), vai pro Painel.
      setTimeout(() => {
        if (user?.must_change_password || u?.must_change_password === false) {
          navigate("/", { replace: true });
        } else {
          navigate("/configuracoes");
        }
      }, 600);
    } catch (err) {
      {const _m=apiError(err); if(_m) toast.error(_m);};
    } finally {
      setSaving(false);
    }
  };

  const sair = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const inputCls =
    "h-11 w-full rounded-md border-2 border-slate-300 px-3 pr-11 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <div data-testid="trocar-senha-page" className="mx-auto max-w-md">
      {!user?.must_change_password && (
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          data-testid="trocar-senha-voltar"
        >
          <ArrowLeft size={14} /> Voltar
        </button>
      )}

      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
          <KeyRound size={26} /> Trocar minha senha
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Você está logado como <strong>{user?.name}</strong> ({user?.email}).
        </p>
      </div>

      {user?.must_change_password && (
        <div className="mb-4 rounded-md border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="must-change-alert">
          Sua conta usa uma <strong>senha provisória</strong>. Troque agora para continuar usando o sistema.
        </div>
      )}

      <form
        onSubmit={submit}
        className="space-y-4 rounded-md border-2 border-slate-200 bg-white p-5"
        data-testid="trocar-senha-form"
      >
        <label className="block text-sm font-semibold text-slate-700">
          Senha atual
          <PasswordInput
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            data-testid="trocar-senha-atual"
            required
            autoComplete="current-password"
            inputClassName={`mt-1 font-normal ${inputCls}`}
            className="mt-1"
          />
        </label>
        <label className="block text-sm font-semibold text-slate-700">
          Nova senha
          <PasswordInput
            value={novo}
            onChange={(e) => setNovo(e.target.value)}
            data-testid="trocar-senha-nova"
            required
            autoComplete="new-password"
            minLength={6}
            inputClassName={`mt-1 font-normal ${inputCls}`}
            className="mt-1"
          />
          <div className="mt-1 text-xs text-slate-400">Mínimo 6 caracteres.</div>
        </label>
        <label className="block text-sm font-semibold text-slate-700">
          Confirmar nova senha
          <PasswordInput
            value={confirmar}
            onChange={(e) => setConfirmar(e.target.value)}
            data-testid="trocar-senha-confirmar"
            required
            autoComplete="new-password"
            inputClassName={`mt-1 font-normal ${inputCls}`}
            className="mt-1"
          />
        </label>
        <button
          type="submit"
          disabled={saving}
          data-testid="trocar-senha-submit"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
          Salvar nova senha
        </button>

        {user?.must_change_password && (
          <button
            type="button"
            onClick={sair}
            data-testid="trocar-senha-sair"
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-md border-2 border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            <LogOut size={16} /> Sair sem trocar
          </button>
        )}
      </form>
    </div>
  );
}
